import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { Radio, Divider, Flex, Space, Stack, Text, Tooltip } from '@mantine/core';

import { isRadioGroupValue } from 'helpers/menuPreview';
import type { ModifierGroupFormValue, PreviewModifier } from 'helpers/menuPreview';

import { ModifierPrice } from './ModifierPrice';
import { NestedModifierIndicator } from './NestedModifierIndicator';

type RadioGroupItemProps = {
  onModifierChange: (groupKey: string, selectedModifier: PreviewModifier | undefined) => void;
  onDrillIn: (modifierKey: string, modifierName: string) => void;
  modifiers: PreviewModifier[];
  groupValue?: ModifierGroupFormValue;
  groupKey: string;
  showTrailingDivider?: boolean;
};

export const RadioGroupItem: FunctionComponent<RadioGroupItemProps> = ({
  onModifierChange,
  onDrillIn,
  modifiers,
  groupValue,
  groupKey,
  showTrailingDivider = true,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(
    groupValue && isRadioGroupValue(groupValue) ? groupValue.key : null,
  );

  const handleRadioChange = (value: string) => {
    setSelectedValue(value);
    const selectedModifier = modifiers.find(mod => mod.key === value);
    onModifierChange(groupKey, selectedModifier);

    if (selectedModifier?.hasNestedGroups) {
      onDrillIn(selectedModifier.key, selectedModifier.name ?? '');
    }
  };

  return (
    <>
      <Radio.Group value={selectedValue} onChange={handleRadioChange}>
        <Stack>
          {modifiers.map(modifier => {
            const isModifierSelected = selectedValue === modifier.key;
            const hasNestedGroups = modifier.hasNestedGroups ?? false;
            const handleDrillIn = () => onDrillIn(modifier.key, modifier.name ?? '');

            return (
              <div key={modifier.uuid}>
                <Flex justify="space-between" align="center" gap="xs">
                  <Stack gap={0} style={{ flexGrow: 1 }}>
                    {modifier.paused ? (
                      <Tooltip label="This modifier is unavailable">
                        <Text c="var(--content-secondary)">{modifier.name}</Text>
                      </Tooltip>
                    ) : (
                      <Text>{modifier.name}</Text>
                    )}
                    <ModifierPrice modifier={modifier} />
                  </Stack>
                  <Flex align="center" gap="xs">
                    <Radio
                      value={modifier.key}
                      disabled={modifier.paused}
                      aria-label={modifier.name}
                    />
                    <NestedModifierIndicator
                      variant="inline"
                      hasNestedGroups={hasNestedGroups}
                      isSelected={isModifierSelected}
                      onDrillIn={handleDrillIn}
                    />
                  </Flex>
                </Flex>
                <NestedModifierIndicator
                  variant="banner"
                  hasNestedGroups={hasNestedGroups}
                  isSelected={isModifierSelected}
                  nestedSummary={modifier.nestedSummary}
                  onDrillIn={handleDrillIn}
                />
              </div>
            );
          })}
        </Stack>
      </Radio.Group>
      {showTrailingDivider ? <Divider my="md" /> : <Space h="xl" />}
    </>
  );
};
